// The CLI interface: a text input and a transcript, nothing else. Where the
// pads afford poking at a sound, this affords *saying* something about it --
// precise, repeatable, and unusable until you know the words. The parameters
// here (autocomplete, verbosity, hidden labels) are the recognition/recall
// dial that week 9 demonstrates, so each one has to be a real difference,
// not a cosmetic one.

import './cli.css'
import { eventTime } from '../clock.js'
import { COMMANDS, helpLines, parse } from './cli-parser.js'

export const meta = {
  id: 'cli',
  label: 'CLI',
  order: 20,
  paramGroup: 'cli',
  blurb: 'type commands: precision and patterning, at the cost of having to know the words',
  studyCondition: true,
}

const MAX_ROWS = 120
const PATTERN_HINTS = ['x...x...x...x...', 'x.x.x.x.x.x.x.x.', '................']

export function mount({ api, root, params }) {
  const ac = new AbortController()
  const { signal } = ac
  const sounds = api.sounds
  let cfg = params.group('cli')
  let unlocked = false

  const history = []
  let historyAt = 0        // == history.length means "at the live line"
  let draft = ''           // what was typed before arrowing into history

  // Onset here is the first character typed after a cue: the CLI equivalent
  // of a finger leaving home. Everything after it is typing time, which is
  // the cost this interface is being measured for.
  const onsetListeners = new Set()
  const cancelListeners = new Set()
  let armed = false

  root.classList.add('cli')
  root.innerHTML = `
    <div class="cli-cue" hidden></div>
    <div class="cli-transcript" role="log" aria-live="polite" aria-label="transcript"></div>
    <div class="cli-strip" hidden></div>
    <form class="cli-form" autocomplete="off">
      <span class="cli-prompt" aria-hidden="true">&gt;</span>
      <div class="cli-field">
        <div class="cli-ghost" aria-hidden="true"><span></span><span></span></div>
        <input class="cli-input" type="text" spellcheck="false" autocapitalize="off"
               autocorrect="off" enterkeyhint="send" aria-label="command">
      </div>
    </form>
    <p class="cli-help hint"></p>
  `

  const cueEl = root.querySelector('.cli-cue')
  const transcript = root.querySelector('.cli-transcript')
  const strip = root.querySelector('.cli-strip')
  const form = root.querySelector('.cli-form')
  const input = root.querySelector('.cli-input')
  const ghost = root.querySelector('.cli-ghost')
  const [ghostTyped, ghostRest] = ghost.children
  const helpLine = root.querySelector('.cli-help')

  // --- transcript ---------------------------------------------------------

  function say(text, kind = 'out') {
    const row = document.createElement('div')
    row.className = `cli-row cli-${kind}`
    row.textContent = text
    transcript.append(row)
    while (transcript.childElementCount > MAX_ROWS) transcript.firstElementChild.remove()
    transcript.scrollTop = transcript.scrollHeight
    return row
  }

  const sayAll = (lines, kind) => lines.forEach((l) => say(l, kind))

  // --- the completion model ------------------------------------------------

  // Hidden labels turn the round into a recall task, so nothing here may
  // offer a sound name the person has not already typed correctly.
  function candidatesFor(tokens) {
    const i = tokens.length - 1
    const cmd = tokens[0]
    if (i === 0) return COMMANDS
    if (cmd === 'help') return i === 1 ? COMMANDS : []
    if (cfg.hiddenLabels) return cmd === 'loop' && i >= 2 ? PATTERN_HINTS : []
    if (cmd === 'play') return i === 1 ? sounds : []
    if (cmd === 'loop') return i === 1 ? sounds : [...PATTERN_HINTS, ...sounds]
    return []
  }

  function tokenise(value) {
    const parts = value.trimStart().split(/\s+/)
    return /\s$/.test(value) ? [...parts.filter(Boolean), ''] : parts
  }

  function completion() {
    if (!cfg.autocomplete) return { matches: [], rest: '' }
    const tokens = tokenise(input.value)
    const cur = tokens[tokens.length - 1]
    const matches = candidatesFor(tokens).filter((c) => c.startsWith(cur) && c !== cur)
    return { matches, cur, rest: matches.length ? matches[0].slice(cur.length) : '' }
  }

  function commonPrefix(words) {
    if (words.length === 0) return ''
    let out = words[0]
    for (const w of words.slice(1)) {
      let i = 0
      while (i < out.length && out[i] === w[i]) i++
      out = out.slice(0, i)
    }
    return out
  }

  function renderCompletion() {
    const { matches, cur, rest } = completion()
    ghostTyped.textContent = rest ? input.value : ''
    ghostRest.textContent = rest
    strip.replaceChildren()
    strip.hidden = matches.length === 0
    for (const m of matches.slice(0, 8)) {
      const b = document.createElement('button')
      b.type = 'button'
      b.className = 'cli-chip'
      b.dataset.word = m
      b.textContent = m
      strip.append(b)
    }
    return { matches, cur }
  }

  function acceptCompletion() {
    const { matches, cur } = completion()
    if (matches.length === 0) return false
    // Shell behaviour: extend to the longest unambiguous prefix first, and
    // only commit to a whole word once that stops adding anything.
    const shared = commonPrefix(matches)
    const word = shared.length > cur.length ? shared : matches[0]
    insertWord(word, matches.length === 1 || word === matches[0])
    return true
  }

  function insertWord(word, addSpace) {
    const tokens = tokenise(input.value)
    tokens[tokens.length - 1] = word
    input.value = tokens.join(' ') + (addSpace ? ' ' : '')
    renderCompletion()
  }

  // --- executing a parsed line --------------------------------------------

  const patternString = (steps) => steps.map((s) => (s ? 'x' : '.')).join('')
  const terse = () => cfg.verbosity === 'terse'

  function run(result, t) {
    switch (result.command) {
      case 'play':
        // The study measures from the keypress, not from when this ran.
        api.trigger(result.sound, { t })
        return terse() ? result.sound : `played ${result.sound}`

      case 'loop': {
        for (const [name, steps] of Object.entries(result.tracks)) {
          api.setTrack(sounds.indexOf(name), steps)
        }
        const shown = Object.entries(result.tracks)
          .map(([name, steps]) => `${name} ${patternString(steps)}`)
          .join('  ')
        if (terse()) return shown
        return api.playing ? shown : `${shown}\nnothing is running yet -- type start`
      }

      case 'tempo':
        api.setTempo(result.bpm)
        return terse() ? String(result.bpm) : `tempo is now ${result.bpm} bpm`

      case 'clear':
        api.clear()
        return terse() ? 'ok' : 'every track is empty'

      case 'start':
        api.start()
        return terse() ? 'ok' : `running at ${Math.round(api.tempo)} bpm`

      case 'stop':
        api.stop()
        return terse() ? 'ok' : 'stopped'

      case 'help':
        sayAll(helpLines({ sounds, ...cfg }, result.topic), 'help')
        return null

      default:
        return null
    }
  }

  function submit(event) {
    const line = input.value
    if (!unlocked) { api.unlock(); unlocked = true }

    const result = parse(line, { sounds, hiddenLabels: cfg.hiddenLabels })
    if (result.empty) { input.value = ''; renderCompletion(); return }

    say(`> ${line.trim()}`, 'echo')
    if (cfg.history) { history.push(line.trim()); historyAt = history.length }

    if (result.ok) {
      const { t } = eventTime(event)
      const reply = run(result, t)
      if (reply !== null) sayAll(reply.split('\n'), 'ok')
    } else if (terse()) {
      say(result.terse, 'err')
    } else {
      say(result.error, 'err')
      if (result.usage) say(result.usage, 'usage')
      if (result.suggestion && !result.usage?.includes(result.suggestion)) {
        say(`try: ${result.suggestion}`, 'usage')
      }
    }

    input.value = ''
    draft = ''
    renderCompletion()
  }

  // --- input handling ------------------------------------------------------

  function recall(delta) {
    if (!cfg.history || history.length === 0) return
    if (historyAt === history.length) draft = input.value
    historyAt = Math.max(0, Math.min(history.length, historyAt + delta))
    input.value = historyAt === history.length ? draft : history[historyAt]
    input.setSelectionRange(input.value.length, input.value.length)
    renderCompletion()
  }

  function cancel(reason) {
    armed = false
    for (const fn of cancelListeners) fn({ reason })
  }

  input.addEventListener('keydown', (e) => {
    if (armed && e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
      armed = false
      const { t, source } = eventTime(e)
      for (const fn of onsetListeners) fn({ t, t_source: source, key: e.key })
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      input.value = ''
      renderCompletion()
      cancel('escape')
      return
    }
    if (e.key === 'Tab' && cfg.autocomplete) {
      if (acceptCompletion()) e.preventDefault()
      return
    }
    if (e.key === 'ArrowUp' && cfg.history) { e.preventDefault(); recall(-1); return }
    if (e.key === 'ArrowDown' && cfg.history) { e.preventDefault(); recall(1); return }
    if (e.key === 'Enter') {
      e.preventDefault()
      submit(e)   // this event, not the form's: its timeStamp is the keypress
    }
  }, { signal })

  input.addEventListener('input', renderCompletion, { signal })

  // Focus lost mid-cue: no keystroke can arrive, so the trial is not a
  // response time any more. Say so rather than logging a long one.
  input.addEventListener('blur', () => { if (armed) cancel('blur') }, { signal })
  input.addEventListener('scroll', () => { ghost.scrollLeft = input.scrollLeft }, { signal })

  // The on-screen keyboard's Go button fires submit without a keydown.
  form.addEventListener('submit', (e) => { e.preventDefault(); submit(e) }, { signal })

  strip.addEventListener('click', (e) => {
    const word = e.target.closest('.cli-chip')?.dataset.word
    if (!word) return
    insertWord(word, true)
    input.focus()
  }, { signal })

  // --- parameters ----------------------------------------------------------

  function applyConfig() {
    input.placeholder = cfg.hiddenLabels
      ? 'type a command'
      : `play kick   |   loop hat x.x.x.x.x.x.x.x.   |   help`
    helpLine.textContent = cfg.hiddenLabels
      ? 'sound names are hidden this round -- type help for the commands'
      : `sounds: ${sounds.join('  ')}   ·   commands: ${COMMANDS.join(' ')}`
    root.classList.toggle('cli-recall', cfg.hiddenLabels)
    renderCompletion()
  }

  const unsubscribe = params.onGroupChange('cli', (next) => {
    const wasHidden = cfg.hiddenLabels
    cfg = next
    applyConfig()
    if (cfg.hiddenLabels !== wasHidden) history.length = 0   // do not leak past answers
  })

  applyConfig()
  sayAll(helpLines({ sounds, ...cfg }), 'help')

  // A phone pops the keyboard over the whole screen on focus, which hides the
  // transcript the person is meant to read; let them ask for it instead.
  if (!matchMedia('(pointer: coarse)').matches) input.focus()

  return {
    unmount() {
      ac.abort()
      unsubscribe()
      onsetListeners.clear()
      cancelListeners.clear()
      root.classList.remove('cli', 'cli-recall')
      root.replaceChildren()
    },

    study: {
      cue(sound) {
        cueEl.hidden = false
        cueEl.textContent = `type: ${sound}`
        say(`cue: ${sound}`, 'cue')
        armed = true
        if (!matchMedia('(pointer: coarse)').matches) input.focus()
        // A half-typed line when the cue lands is the CLI's version of a
        // finger already off home, and it contaminates the same measure.
        return { down_before_cue: input.value.length > 0 }
      },

      clearCue() {
        cueEl.hidden = true
        cueEl.textContent = ''
        armed = false
      },

      onOnset(fn) { onsetListeners.add(fn); return () => onsetListeners.delete(fn) },
      onCancel(fn) { cancelListeners.add(fn); return () => cancelListeners.delete(fn) },

      // Nothing spatial to return to, so there is no home to set.
      setHome() {},
      get hasHome() { return false },

      // No spatial target to measure, but the CLI has its own cost model:
      // the number of characters between the cue and a correct response,
      // and whether the machine was willing to type any of them.
      geometry(sound) {
        return {
          region: 'input',
          target_chars: `play ${sound}`.length,
          autocomplete: cfg.autocomplete,
          hidden_labels: cfg.hiddenLabels,
          verbosity: cfg.verbosity,
        }
      },
    },
  }
}
